import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Plus, Search, Users, Layout, Edit3, Paperclip, MessageSquare, Calendar, Flag, MoreVertical, X, Check, User, Moon, Sun, Filter, Bell, Settings, Trash2, Copy, Share2, Tag, Clock, Star, Hash } from 'lucide-react';

export default function App() {
  const [darkMode, setDarkMode] = useState(false);
  const [activeTab, setActiveTab] = useState('board');
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [showNewTask, setShowNewTask] = useState(false);
  const [draggedTask, setDraggedTask] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [showNewUser, setShowNewUser] = useState(false);
  const [newUserData, setNewUserData] = useState({ name: '', avatar: '' });
  const [newTaskData, setNewTaskData] = useState({
    title: '',
    description: '',
    assignee: null,
    priority: 'medium',
    dueDate: '',
    checklist: [],
    comments: [],
    attachments: []
  });

  useEffect(() => {
    const savedTasks = localStorage.getItem('kanban-tasks');
    const savedDarkMode = localStorage.getItem('kanban-darkmode');
    const savedUsers = localStorage.getItem('kanban-users');
    if (savedTasks) setTasks(JSON.parse(savedTasks));
    if (savedDarkMode) setDarkMode(JSON.parse(savedDarkMode));
    if (savedUsers) setUsers(JSON.parse(savedUsers));
  }, []);

  useEffect(() => {
    localStorage.setItem('kanban-tasks', JSON.stringify(tasks));
  }, [tasks]);

  useEffect(() => {
    localStorage.setItem('kanban-darkmode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    localStorage.setItem('kanban-users', JSON.stringify(users));
  }, [users]);

  const lanes = [
    { id: 'todo', title: 'To-Do', color: 'from-slate-600 to-slate-700' },
    { id: 'progress', title: 'In Progress', color: 'from-blue-500 to-indigo-600' },
    { id: 'done', title: 'Done', color: 'from-green-500 to-emerald-600' }
  ];

  const priorityColors = {
    low: 'bg-gradient-to-r from-gray-400 to-gray-500',
    medium: 'bg-gradient-to-r from-yellow-400 to-orange-500',
    high: 'bg-gradient-to-r from-red-500 to-pink-600',
    urgent: 'bg-gradient-to-r from-red-600 to-purple-600'
  };

  const userColors = [
    'bg-gradient-to-br from-purple-500 to-pink-500',
    'bg-gradient-to-br from-blue-500 to-cyan-500',
    'bg-gradient-to-br from-green-500 to-emerald-500',
    'bg-gradient-to-br from-orange-500 to-red-500',
    'bg-gradient-to-br from-indigo-500 to-purple-500',
    'bg-gradient-to-br from-teal-500 to-green-500',
    'bg-gradient-to-br from-pink-500 to-rose-500',
    'bg-gradient-to-br from-yellow-500 to-orange-500'
  ];

  const createTask = () => {
    if (!newTaskData.title) return;
    const task = {
      id: Date.now(),
      ...newTaskData,
      lane: 'todo',
      createdAt: new Date().toISOString(),
      comments: [],
      checklist: [],
      attachments: []
    };
    setTasks([...tasks, task]);
    setShowNewTask(false);
    setNewTaskData({
      title: '',
      description: '',
      assignee: null,
      priority: 'medium',
      dueDate: '',
      checklist: [],
      comments: [],
      attachments: []
    });
  };

  const createUser = () => {
    if (!newUserData.name || !newUserData.avatar) return;
    const user = {
      id: Date.now(),
      name: newUserData.name,
      avatar: newUserData.avatar.toUpperCase(),
      color: userColors[users.length % userColors.length]
    };
    setUsers([...users, user]);
    setShowNewUser(false);
    setNewUserData({ name: '', avatar: '' });
  };

  const deleteUser = (userId) => {
    setUsers(users.filter(user => user.id !== userId));
    setTasks(tasks.map(task => 
      task.assignee?.id === userId ? { ...task, assignee: null } : task
    ));
  };

  const updateTaskLane = (taskId, newLane) => {
    setTasks(tasks.map(task => 
      task.id === taskId ? { ...task, lane: newLane } : task
    ));
  };

  const deleteTask = (taskId) => {
    setTasks(tasks.filter(task => task.id !== taskId));
  };

  const toggleChecklistItem = (taskId, itemIndex) => {
    setTasks(tasks.map(task => {
      if (task.id === taskId) {
        const newChecklist = [...task.checklist];
        newChecklist[itemIndex].completed = !newChecklist[itemIndex].completed;
        return { ...task, checklist: newChecklist };
      }
      return task;
    }));
  };

  const addComment = (taskId, comment) => {
    setTasks(tasks.map(task => {
      if (task.id === taskId) {
        return {
          ...task,
          comments: [...task.comments, {
            id: Date.now(),
            text: comment,
            author: 'Current User',
            timestamp: new Date().toISOString()
          }]
        };
      }
      return task;
    }));
  };

  const TaskCard = ({ task }) => {
    const [showMenu, setShowMenu] = useState(false);
    const [showComments, setShowComments] = useState(false);
    const [newComment, setNewComment] = useState('');
    const cardRef = useRef(null);

    const handleSwipe = (e) => {
      const touch = e.touches[0];
      const startX = touch.clientX;
      let currentX;

      const handleTouchMove = (e) => {
        currentX = e.touches[0].clientX;
        const diff = currentX - startX;
        if (Math.abs(diff) > 50) {
          if (diff > 0 && task.lane !== 'todo') {
            const newLane = task.lane === 'progress' ? 'todo' : 'progress';
            updateTaskLane(task.id, newLane);
          } else if (diff < 0 && task.lane !== 'done') {
            const newLane = task.lane === 'todo' ? 'progress' : 'done';
            updateTaskLane(task.id, newLane);
          }
          cardRef.current.removeEventListener('touchmove', handleTouchMove);
        }
      };

      cardRef.current.addEventListener('touchmove', handleTouchMove);
    };

    return (
      <div
        ref={cardRef}
        className={`relative rounded-3xl p-6 mb-4 shadow-2xl transform transition-all duration-300 hover:scale-105 active:scale-95 ${
          darkMode ? 'bg-gradient-to-br from-gray-800 to-gray-900 text-white' : 'bg-gradient-to-br from-white to-gray-100 border border-gray-200'
        }`}
        onTouchStart={handleSwipe}
      >
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-bold text-lg bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            {task.title}
          </h3>
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            <MoreVertical className="w-5 h-5" />
          </button>
        </div>

        {showMenu && (
          <div className={`absolute right-4 top-16 rounded-2xl shadow-2xl p-2 z-20 ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <button
              onClick={() => deleteTask(task.id)}
              className="flex items-center space-x-2 p-3 hover:bg-red-100 dark:hover:bg-red-900 rounded-xl transition-colors w-full"
            >
              <Trash2 className="w-4 h-4 text-red-500" />
              <span className="text-red-500">Delete</span>
            </button>
          </div>
        )}

        <p className={`text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          {task.description}
        </p>

        <div className="flex items-center justify-between mb-4">
          <span className={`px-3 py-1 rounded-full text-xs font-bold text-white ${priorityColors[task.priority]}`}>
            {task.priority.toUpperCase()}
          </span>
          {task.dueDate && (
            <div className="flex items-center space-x-1 text-sm">
              <Calendar className="w-4 h-4 text-gray-500" />
              <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                {new Date(task.dueDate).toLocaleDateString()}
              </span>
            </div>
          )}
        </div>

        {task.assignee && (
          <div className="flex items-center space-x-2 mb-4">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${task.assignee.color}`}>
              {task.assignee.avatar}
            </div>
            <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {task.assignee.name}
            </span>
          </div>
        )}

        {task.checklist.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center space-x-2 mb-2">
              <Check className="w-4 h-4 text-green-500" />
              <span className="text-sm font-semibold">Checklist</span>
            </div>
            {task.checklist.map((item, index) => (
              <div key={index} className="flex items-center space-x-2 mb-1">
                <button
                  onClick={() => toggleChecklistItem(task.id, index)}
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                    item.completed ? 'bg-green-500 border-green-500' : 'border-gray-400'
                  }`}
                >
                  {item.completed && <Check className="w-3 h-3 text-white" />}
                </button>
                <span className={`text-sm ${item.completed ? 'line-through text-gray-500' : ''}`}>
                  {item.text}
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowComments(!showComments)}
              className="flex items-center space-x-1 text-sm hover:text-blue-500 transition-colors"
            >
              <MessageSquare className="w-4 h-4" />
              <span>{task.comments.length}</span>
            </button>
            <div className="flex items-center space-x-1 text-sm">
              <Paperclip className="w-4 h-4" />
              <span>{task.attachments.length}</span>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
              <Edit3 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {showComments && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="space-y-2 mb-3 max-h-32 overflow-y-auto">
              {task.comments.map(comment => (
                <div key={comment.id} className="text-sm">
                  <span className="font-semibold">{comment.author}:</span> {comment.text}
                </div>
              ))}
            </div>
            <div className="flex space-x-2">
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                className={`flex-1 px-3 py-2 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100'
                }`}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && newComment.trim()) {
                    addComment(task.id, newComment);
                    setNewComment('');
                  }
                }}
              />
              <button
                onClick={() => {
                  if (newComment.trim()) {
                    addComment(task.id, newComment);
                    setNewComment('');
                  }
                }}
                className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl text-sm font-semibold hover:shadow-lg transition-all"
              >
                Send
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const KanbanLane = ({ lane }) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const laneTasks = tasks.filter(task => task.lane === lane.id);

    const handleDragOver = (e) => {
      e.preventDefault();
      setIsDragOver(true);
    };

    const handleDragLeave = () => {
      setIsDragOver(false);
    };

    const handleDrop = (e) => {
      e.preventDefault();
      setIsDragOver(false);
      if (draggedTask) {
        updateTaskLane(draggedTask, lane.id);
        setDraggedTask(null);
      }
    };

    return (
      <div
        className={`flex-1 rounded-3xl p-6 transition-all duration-300 ${
          isDragOver ? 'ring-4 ring-blue-400 ring-opacity-50' : ''
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className={`bg-gradient-to-br ${lane.color} rounded-2xl p-4 mb-6 shadow-xl`}>
          <h2 className="text-white font-bold text-xl">{lane.title}</h2>
          <span className="text-white/80 text-sm">{laneTasks.length} tasks</span>
        </div>
        
        <div className="space-y-4">
          {laneTasks.map(task => (
            <div
              key={task.id}
              draggable
              onDragStart={() => setDraggedTask(task.id)}
              className="cursor-move"
            >
              <TaskCard task={task} />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const NewTaskModal = () => {
    if (!showNewTask) return null;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-md z-50 flex items-center justify-center p-4">
        <div className={`w-full max-w-md rounded-3xl shadow-2xl p-6 max-h-[90vh] overflow-y-auto ${
          darkMode ? 'bg-gradient-to-br from-gray-800 to-gray-900 text-white' : 'bg-gradient-to-br from-white to-gray-50'
        }`}>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              New Task
            </h2>
            <button
              onClick={() => setShowNewTask(false)}
              className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-4">
            <input
              type="text"
              placeholder="Task title"
              value={newTaskData.title}
              onChange={(e) => setNewTaskData({...newTaskData, title: e.target.value})}
              className={`w-full px-4 py-3 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100'
              }`}
            />

            <textarea
              placeholder="Description"
              value={newTaskData.description}
              onChange={(e) => setNewTaskData({...newTaskData, description: e.target.value})}
              className={`w-full px-4 py-3 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 h-24 resize-none ${
                darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100'
              }`}
            />

            <select
              value={newTaskData.priority}
              onChange={(e) => setNewTaskData({...newTaskData, priority: e.target.value})}
              className={`w-full px-4 py-3 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100'
              }`}
            >
              <option value="low">Low Priority</option>
              <option value="medium">Medium Priority</option>
              <option value="high">High Priority</option>
              <option value="urgent">Urgent</option>
            </select>

            <input
              type="date"
              value={newTaskData.dueDate}
              onChange={(e) => setNewTaskData({...newTaskData, dueDate: e.target.value})}
              className={`w-full px-4 py-3 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100'
              }`}
            />

            <div>
              <label className="block text-sm font-semibold mb-2">Assignee</label>
              <div className="grid grid-cols-2 gap-2">
                {users.map(user => (
                  <button
                    key={user.id}
                    onClick={() => setNewTaskData({...newTaskData, assignee: user})}
                    className={`flex items-center space-x-2 p-3 rounded-2xl transition-all ${
                      newTaskData.assignee?.id === user.id
                        ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                        : darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${user.color}`}>
                      {user.avatar}
                    </div>
                    <span className="text-sm">{user.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <button
            onClick={createTask}
            className="w-full mt-6 bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 rounded-2xl font-bold text-lg hover:shadow-2xl transform hover:scale-105 active:scale-95 transition-all"
          >
            Create Task
          </button>
        </div>
      </div>
    );
  };

  const NewUserModal = () => {
    if (!showNewUser) return null;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-md z-50 flex items-center justify-center p-4">
        <div className={`w-full max-w-md rounded-3xl shadow-2xl p-6 ${
          darkMode ? 'bg-gradient-to-br from-gray-800 to-gray-900 text-white' : 'bg-gradient-to-br from-white to-gray-50'
        }`}>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Add Team Member
            </h2>
            <button
              onClick={() => setShowNewUser(false)}
              className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-4">
            <input
              type="text"
              placeholder="Full Name"
              value={newUserData.name}
              onChange={(e) => setNewUserData({...newUserData, name: e.target.value})}
              className={`w-full px-4 py-3 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100'
              }`}
            />

            <input
              type="text"
              placeholder="Initials (e.g., JD)"
              value={newUserData.avatar}
              onChange={(e) => setNewUserData({...newUserData, avatar: e.target.value.slice(0, 2)})}
              className={`w-full px-4 py-3 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100'
              }`}
              maxLength="2"
            />
          </div>

          <button
            onClick={createUser}
            className="w-full mt-6 bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 rounded-2xl font-bold text-lg hover:shadow-2xl transform hover:scale-105 active:scale-95 transition-all"
          >
            Add Member
          </button>
        </div>
      </div>
    );
  };

  const PeopleView = () => (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
          Team Members
        </h2>
        <button
          onClick={() => setShowNewUser(true)}
          className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-3 rounded-2xl hover:shadow-xl transform hover:scale-105 active:scale-95 transition-all"
        >
          <Plus className="w-6 h-6" />
        </button>
      </div>
      <div className="space-y-4">
        {users.map(user => {
          const userTasks = tasks.filter(task => task.assignee?.id === user.id);
          return (
            <div
              key={user.id}
              className={`rounded-3xl p-6 shadow-xl transform transition-all duration-300 hover:scale-105 active:scale-95 ${
                darkMode ? 'bg-gradient-to-br from-gray-800 to-gray-900 text-white' : 'bg-gradient-to-br from-white to-gray-100 border border-gray-200'
              }`}
            >
              <div className="flex items-center space-x-4">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-xl ${user.color}`}>
                  {user.avatar}
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-lg">{user.name}</h3>
                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {userTasks.length} tasks assigned
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-green-500">
                    {userTasks.filter(t => t.lane === 'done').length}
                  </div>
                  <p className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Completed</p>
                </div>
                <button
                  onClick={() => deleteUser(user.id)}
                  className="p-2 rounded-full hover:bg-red-100 dark:hover:bg-red-900 transition-colors"
                >
                  <Trash2 className="w-5 h-5 text-red-500" />
                </button>
              </div>
            </div>
          );
        })}
        {users.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              No team members yet. Add your first member!
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const SearchView = () => {
    const filteredTasks = tasks.filter(task =>
      task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      task.description.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
      <div className="p-6">
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`w-full pl-12 pr-4 py-4 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg ${
              darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100'
            }`}
          />
        </div>

        <div className="space-y-4">
          {filteredTasks.map(task => (
            <TaskCard key={task.id} task={task} />
          ))}
          {filteredTasks.length === 0 && (
            <div className="text-center py-12">
              <Search className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                No tasks found
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      darkMode ? 'bg-gradient-to-br from-gray-900 to-black' : 'bg-gradient-to-br from-blue-50 to-purple-50'
    }`}>
      {/* Header */}
      <header className={`sticky top-0 z-40 backdrop-blur-md border-b transition-colors ${
        darkMode ? 'bg-gray-900/95 border-gray-700' : 'bg-white/95 border-gray-200'
      }`}>
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center">
              <Layout className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              KanbanFlow
            </h1>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              <Bell className="w-6 h-6" />
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></span>
            </button>
            <button
              onClick={() => setDarkMode(!darkMode)}
              className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              {darkMode ? <Sun className="w-6 h-6 text-yellow-500" /> : <Moon className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pb-24">
        {activeTab === 'board' && (
          <div className="p-6">
            <div className="flex space-x-6 overflow-x-auto pb-4">
              {lanes.map(lane => (
                <KanbanLane key={lane.id} lane={lane} />
              ))}
            </div>
          </div>
        )}
        
        {activeTab === 'new' && <NewTaskModal />}
        {activeTab === 'people' && <PeopleView />}
        {activeTab === 'search' && <SearchView />}
      </main>

      {/* Bottom Navigation */}
      <nav className={`fixed bottom-0 left-0 right-0 backdrop-blur-md border-t z-30 ${
        darkMode ? 'bg-gray-900/95 border-gray-700' : 'bg-white/95 border-gray-200'
      }`}>
        <div className="flex items-center justify-around py-3">
          {[
            { id: 'board', icon: Layout, label: 'Board' },
            { id: 'new', icon: Plus, label: 'New Issue' },
            { id: 'people', icon: Users, label: 'People' },
            { id: 'search', icon: Search, label: 'Search' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-col items-center space-y-1 px-4 py-2 rounded-2xl transition-all duration-300 ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                  : darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <tab.icon className="w-6 h-6" />
              <span className="text-xs font-semibold">{tab.label}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Floating Action Button for Quick Create */}
      <button
        onClick={() => setShowNewTask(true)}
        className="fixed bottom-24 right-6 w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full shadow-2xl flex items-center justify-center transform transition-all duration-300 hover:scale-110 active:scale-95"
      >
        <Plus className="w-8 h-8 text-white" />
      </button>

      <NewTaskModal />
      <NewUserModal />
    </div>
  );
}